import { AlertTriangle, ChevronDown, Copy, RotateCcw, Undo2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatRail } from "@/shared/utils/railDisplay";
import { colocaToast } from "@/shared/utils/toast";

import { usePayoutRescueDetail } from "../hooks/usePayoutRescueDetail";
import { PayoutStatus } from "../services/payoutRescue.service";
import type { RescuePayout } from "../types/payoutRescue.types";
import {
  destinationBeneficiary,
  destinationLabel,
  destinationValue,
  formatMoney,
  maskDestination,
  shortId,
} from "../utils/payoutRescueFormat";
import { RescueStatusPill } from "./RescueStatusPill";
import { RescueTimeline } from "./RescueTimeline";

export interface RescueDrawerProps {
  payout: RescuePayout;
  orgName: string | null;
  open: boolean;
  onClose: () => void;
  onRetry: (payout: RescuePayout) => void;
  onCancel: (payout: RescuePayout) => void;
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

function MetaRow({
  label,
  value,
  copyValue,
  mono,
}: {
  label: string;
  value: string | null;
  /** When set, a copy button puts THIS raw value on the clipboard (masked destinations copy in full). */
  copyValue?: string;
  mono?: boolean;
}) {
  const { t } = useTranslations("payoutRescue");
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex min-w-0 items-center gap-1">
        <span
          className={cn("truncate text-sm text-foreground", mono ? "font-mono text-xs" : "font-medium")}
          title={value}
        >
          {value}
        </span>
        {copyValue && (
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            aria-label={t("copy_aria")}
            onClick={() => {
              void navigator.clipboard
                .writeText(copyValue)
                .then(() => colocaToast.success(t("copy_success")))
                .catch(() => undefined);
            }}
          />
        )}
      </span>
    </div>
  );
}

function AmountRow({ label, value, emphasize }: { label: string; value: ReactNode; emphasize?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className={emphasize ? "text-sm font-semibold text-foreground" : "text-xs text-muted-foreground"}>
        {label}
      </span>
      <span
        className={
          emphasize
            ? "font-mono text-base font-bold text-primary tabular-nums"
            : "font-mono text-sm text-foreground tabular-nums"
        }
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Detail drawer of one payout: full money breakdown (immutable `fee_breakdown` snapshot), masked destination
 * when the snapshot is exposed, and the append-only audit timeline (who retried it and when). Actions follow
 * the state machine strictly — retry/cancel only on CREATED; PROCESSING is in flight (a reconciliation tool
 * is a future backend slice — disabled affordance only); terminal rows are read-only and FAILED never shows a
 * retry. `provider_code` is never rendered; `provider_reference` shows as an opaque copyable reference.
 */
export function RescueDrawer({
  payout: initialPayout,
  orgName,
  open,
  onClose,
  onRetry,
  onCancel,
}: RescueDrawerProps) {
  const { t, activeLanguage } = useTranslations("payoutRescue");
  const taxLabel = useTaxLabel();
  const { payout, timeline, isTimelineLoading, hasTimelineError, actorLabels } =
    usePayoutRescueDetail(initialPayout);
  const [feesOpen, setFeesOpen] = useState(false);

  const money = (value: string | null | undefined) =>
    value != null ? `${formatMoney(value, activeLanguage)} ${payout.currency}` : "—";

  const isCreated = payout.status === PayoutStatus.CREATED;
  const isProcessing = payout.status === PayoutStatus.PROCESSING;
  const isNonTerminal = isCreated || isProcessing;

  const corridor = `${payout.country} · ${formatRail(payout.rail)} · ${payout.currency}`;
  const beneficiary = destinationBeneficiary(payout.destination);
  const destLabel = payout.destination ? destinationLabel(payout.destination) : null;
  const breakdown = payout.fee_breakdown;
  const colocaFee = breakdown?.coloca?.total;
  const networkFee = breakdown?.provider?.total;
  const taxes = breakdown?.taxes ?? [];
  const hasFeeDetail =
    colocaFee != null || networkFee != null || breakdown?.economic_model != null || taxes.length > 0;

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="md"
      title={t("drawer_title", { id: shortId(payout.id) })}
      description={corridor}
      footer={
        isCreated ? (
          <div className="flex w-full gap-2">
            <ColocaButton
              variant="destructive"
              icon={Undo2}
              className="flex-1"
              onClick={() => onCancel(payout)}
            >
              {t("actions_cancel")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              icon={RotateCcw}
              className="flex-1"
              onClick={() => onRetry(payout)}
            >
              {t("actions_retry")}
            </ColocaButton>
          </div>
        ) : isProcessing ? (
          <div className="w-full space-y-2 text-center">
            <p className="text-xs text-muted-foreground">{t("processing_note")}</p>
            {/* Stuck-PROCESSING reconciliation is a future backend slice — visual room, no flow. */}
            <ColocaButton variant="default" className="w-full" disabled>
              {t("reconcile_soon")}
            </ColocaButton>
          </div>
        ) : (
          <p className="w-full text-center text-xs text-muted-foreground">{t("terminal_note")}</p>
        )
      }
    >
      {/* Status header */}
      <div className="mb-4 rounded-xl border border-border bg-muted/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <RescueStatusPill status={payout.status} long />
          <span className="font-mono text-[10px] text-muted-foreground">{payout.status}</span>
        </div>
        {isCreated && (
          <div className="mt-3">
            <ColocaAlert variant="warning" icon={AlertTriangle} text={t("drawer_rescue_alert")} />
          </div>
        )}
        {payout.failure_reason && !isCreated && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("drawer_failure_reason")}: <span className="italic">“{payout.failure_reason}”</span>
          </p>
        )}
      </div>

      {/* Identity */}
      <div className="mb-4 rounded-xl border border-border bg-card p-4">
        <div className="divide-y divide-border/60">
          <MetaRow label={t("drawer_org")} value={orgName ?? shortId(payout.organization_id)} />
          <MetaRow label={t("drawer_corridor")} value={corridor} />
          {beneficiary && <MetaRow label={t("drawer_beneficiary")} value={beneficiary} />}
          {payout.destination && destLabel ? (
            <MetaRow
              label={t(destLabel.key, destLabel.params)}
              value={maskDestination(payout.destination)}
              copyValue={destinationValue(payout.destination)}
              mono
            />
          ) : (
            // The per-rail destination snapshot is not exposed by the API yet (backend slice pending).
            <div className="py-1.5">
              <p className="text-xs italic text-muted-foreground">{t("drawer_destination_unavailable")}</p>
            </div>
          )}
          <MetaRow label={t("drawer_concept")} value={payout.concept} />
          <MetaRow
            label={t("drawer_reference")}
            value={payout.provider_reference}
            copyValue={payout.provider_reference ?? undefined}
            mono
          />
          <MetaRow label={t("drawer_created")} value={formatDateTime(payout.created_at)} />
        </div>
      </div>

      {/* Money breakdown */}
      <div className="mb-4 rounded-xl border border-border bg-card p-4">
        <SectionTitle>{t("breakdown_title")}</SectionTitle>
        <div className="divide-y divide-border/60">
          <AmountRow label={t("breakdown_gross")} value={money(payout.gross_amount)} />
          <div>
            <div className="flex items-baseline justify-between gap-4 py-1.5">
              {hasFeeDetail ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setFeesOpen((v) => !v)}
                  aria-expanded={feesOpen}
                >
                  {t("breakdown_fees")}
                  <ChevronDown
                    className={cn("h-3 w-3 transition-transform", feesOpen && "rotate-180")}
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">{t("breakdown_fees")}</span>
              )}
              <span className="font-mono text-sm text-foreground tabular-nums">
                {money(payout.fee_total)}
              </span>
            </div>
            {feesOpen && hasFeeDetail && (
              <div className="mb-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                {colocaFee != null && (
                  <div className="flex items-baseline justify-between py-1">
                    <span className="text-xs text-muted-foreground">{t("breakdown_fee_coloca")}</span>
                    <span className="font-mono text-xs tabular-nums">{money(colocaFee)}</span>
                  </div>
                )}
                {networkFee != null && (
                  <div className="flex items-baseline justify-between py-1">
                    <span className="text-xs text-muted-foreground">{t("breakdown_fee_network")}</span>
                    <span className="font-mono text-xs tabular-nums">{money(networkFee)}</span>
                  </div>
                )}
                {taxes.map((tax) => (
                  <div key={tax.tax_config_id} className="flex items-baseline justify-between py-1">
                    <span className="text-xs text-muted-foreground">
                      {taxLabel(tax)}
                      {tax.bearer === "COLOCA" && ` · ${t("breakdown_tax_absorbed")}`}
                    </span>
                    <span className="font-mono text-xs tabular-nums">{money(tax.total)}</span>
                  </div>
                ))}
                {breakdown?.economic_model && (
                  <p className="mt-1 border-t border-border pt-1.5 text-[11px] text-muted-foreground">
                    {t("breakdown_economic_model")}: {breakdown.economic_model}
                  </p>
                )}
              </div>
            )}
          </div>
          <AmountRow label={t("breakdown_net")} value={money(payout.net_amount)} emphasize />
        </div>
      </div>

      {/* Audit timeline */}
      <div className="rounded-xl border border-border bg-card p-4">
        <SectionTitle>{t("timeline_title")}</SectionTitle>
        <RescueTimeline
          timeline={timeline}
          isLoading={isTimelineLoading}
          hasError={hasTimelineError}
          actorLabels={actorLabels}
          isNonTerminal={isNonTerminal}
        />
      </div>
    </ColocaDrawer>
  );
}
